import { NavLink, useLocation, useNavigate } from "react-router-dom"
import {
  LayoutDashboard, Users, Package, DollarSign, Wallet,
  FileText, ArrowDownToLine, ShieldCheck, Settings,
  LogOut, Zap, Clock, Ban, ArrowLeftRight,
  TrendingUp, Building2, Megaphone, SlidersHorizontal,
} from "lucide-react"
import { clsx } from "clsx"
import { useToast } from "@/context/ToastContext"
import { supabase } from "@/lib/supabase"

interface NavItem {
  label:  string
  to:     string
  icon:   React.ReactNode
  badge?: number
  end?:   boolean
}

interface NavSection {
  title: string
  items: NavItem[]
}

const NAV: NavSection[] = [
  {
    title: "Overview",
    items: [
      { label: "Dashboard",     to: "/",              icon: <LayoutDashboard size={15} />, end: true },
      { label: "Products",      to: "/products",      icon: <Package size={15} /> },
      { label: "Company Slots", to: "/company-slots", icon: <Building2 size={15} /> },
      { label: "Earnings",      to: "/earnings",      icon: <TrendingUp size={15} /> },
    ],
  },
  {
    title: "User Management",
    items: [
      { label: "All Users",    to: "/users",         icon: <Users size={15} /> },
      { label: "Grace Periods",to: "/grace-periods", icon: <Clock size={15} /> },
      { label: "Suspended",    to: "/suspended",     icon: <Ban size={15} /> },
      { label: "Transfers",    to: "/transfers",     icon: <ArrowLeftRight size={15} /> },
    ],
  },
  {
    title: "Financial",
    items: [
      { label: "Commissions",  to: "/commissions",   icon: <DollarSign size={15} /> },
      { label: "Adjustments",  to: "/adjustments",   icon: <SlidersHorizontal size={15} /> },
      { label: "Payout Queue", to: "/payouts",       icon: <Zap size={15} /> },
      { label: "Wallets",      to: "/wallets",       icon: <Wallet size={15} /> },
      { label: "Bank Accounts",to: "/bank-accounts", icon: <ArrowDownToLine size={15} /> },
      { label: "W-9 Forms",    to: "/w9",            icon: <FileText size={15} /> },
      { label: "NACHA Batches",to: "/batches",       icon: <Zap size={15} /> },
    ],
  },
  {
    title: "Communication",
    items: [
      { label: "Announcements",to: "/announcements", icon: <Megaphone size={15} /> },
      { label: "Audit Log",    to: "/audit",         icon: <ShieldCheck size={15} /> },
    ],
  },
  {
    title: "System",
    items: [
      { label: "Comm. Rates",  to: "/rates", icon: <SlidersHorizontal size={15} /> },
      { label: "Settings",     to: "/settings",         icon: <Settings size={15} /> },
    ],
  },
]

export function Sidebar() {
  const location  = useLocation()
  const navigate  = useNavigate()
  const { toast } = useToast()

  const handleSignOut = async () => {
    try {
      await supabase.auth.signOut()
      navigate("/login")
    } catch {
      toast("Sign out failed", "error")
    }
  }

  return (
    <aside style={{
      width: 220,
      minHeight: "100vh",
      background: "#1E1B4B",
      display: "flex",
      flexDirection: "column",
      flexShrink: 0,
      borderRight: "1px solid rgba(255,255,255,0.06)",
    }}>
      {/* Logo */}
      <div style={{
        padding: "20px 20px 16px",
        borderBottom: "1px solid rgba(255,255,255,0.06)",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{
            width: 32, height: 32, borderRadius: 8,
            background: "linear-gradient(135deg, #4F46E5, #7C3AED)",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 14, fontWeight: 900, color: "white",
          }}>Z</div>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: "white", letterSpacing: "-0.01em" }}>
              Zimrah
            </div>
            <div style={{ fontSize: 10, color: "rgba(255,255,255,0.4)", letterSpacing: "0.08em", textTransform: "uppercase" }}>
              Admin Console
            </div>
          </div>
        </div>
      </div>

      {/* Nav */}
      <nav style={{ flex: 1, padding: "12px 0", overflowY: "auto" }}>
        {NAV.map(section => (
          <div key={section.title} style={{ marginBottom: 4 }}>
            <div style={{
              padding: "8px 20px 4px",
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: "0.1em",
              textTransform: "uppercase",
              color: "rgba(255,255,255,0.25)",
            }}>
              {section.title}
            </div>
            {section.items.map(item => {
              const isActive = item.end
                ? location.pathname === item.to
                : location.pathname.startsWith(item.to)
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 9,
                    padding: "7px 20px",
                    fontSize: 13,
                    fontWeight: isActive ? 600 : 400,
                    color: isActive ? "white" : "rgba(255,255,255,0.55)",
                    background: isActive ? "rgba(79,70,229,0.25)" : "transparent",
                    borderRight: isActive ? "3px solid #4F46E5" : "3px solid transparent",
                    textDecoration: "none",
                    transition: "all 0.1s ease",
                    cursor: "pointer",
                  }}
                  onMouseEnter={e => {
                    if (!isActive) {
                      (e.currentTarget as HTMLElement).style.color = "rgba(255,255,255,0.85)"
                      ;(e.currentTarget as HTMLElement).style.background = "rgba(255,255,255,0.05)"
                    }
                  }}
                  onMouseLeave={e => {
                    if (!isActive) {
                      (e.currentTarget as HTMLElement).style.color = "rgba(255,255,255,0.55)"
                      ;(e.currentTarget as HTMLElement).style.background = "transparent"
                    }
                  }}
                >
                  <span style={{ opacity: isActive ? 1 : 0.6, flexShrink: 0 }}>{item.icon}</span>
                  <span style={{ flex: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {item.label}
                  </span>
                  {item.badge != null && item.badge > 0 && (
                    <span style={{
                      background: "#4F46E5",
                      color: "white",
                      fontSize: 10,
                      fontWeight: 700,
                      padding: "1px 6px",
                      borderRadius: 10,
                      flexShrink: 0,
                    }}>
                      {item.badge}
                    </span>
                  )}
                </NavLink>
              )
            })}
          </div>
        ))}
      </nav>

      {/* Footer */}
      <div style={{
        padding: "12px 16px",
        borderTop: "1px solid rgba(255,255,255,0.06)",
      }}>
        <button
          onClick={handleSignOut}
          style={{
            display: "flex", alignItems: "center", gap: 8,
            width: "100%", padding: "8px 10px",
            background: "rgba(220,38,38,0.1)",
            border: "1px solid rgba(220,38,38,0.2)",
            borderRadius: 8,
            color: "rgba(252,165,165,0.9)",
            fontSize: 13, fontWeight: 500,
            cursor: "pointer",
            transition: "all 0.15s ease",
          }}
          onMouseEnter={e => {
            (e.currentTarget as HTMLElement).style.background = "rgba(220,38,38,0.2)"
          }}
          onMouseLeave={e => {
            (e.currentTarget as HTMLElement).style.background = "rgba(220,38,38,0.1)"
          }}
        >
          <LogOut size={14} />
          Sign Out
        </button>
      </div>
    </aside>
  )
}